<template>
  <Modal
    width="420"
    :value="value"
    :title="$t('resources.pages.additional_sections_replace_modal_title')"
    content-class="replace-content-modal"
    @cancel="$emit('input', false)">
    <div class="replace-content">
      <div class="replace-content__panel">
        <div class="replace-content__field">
          <span class="replace-content__label">{{ $t("resources.pages.additional_sections_replace_search") }}</span>
          <v-text-field v-model="search" outlined dense hide-details class="replace-content__input"></v-text-field>
        </div>
        <v-text-field
          v-model="replace"
          outlined
          dense
          hide-details
          class="replace-content__input"
          :placeholder="$t('resources.pages.additional_sections_replace_with')"></v-text-field>

        <div class="replace-content__count">
          {{ $t("resources.pages.additional_sections_replace_results", { count: results.total }) }}
        </div>

        <div class="replace-content__divider"></div>

        <div v-if="results.groups.length" class="replace-content__results">
          <div v-for="group in results.groups" :key="group.key" class="replace-content__group">
            <div class="replace-content__group-head" @click="toggleGroup(group.key)">
              <v-icon size="20" class="replace-content__group-chevron">
                {{ isGroupOpen(group.key) ? "mdi-chevron-down" : "mdi-chevron-right" }}
              </v-icon>
              <span class="replace-content__group-label">{{ group.label }}</span>
            </div>
            <div v-if="isGroupOpen(group.key)" class="replace-content__snippets">
              <p v-for="(snippet, index) in group.snippets" :key="index" class="replace-content__snippet">
                <span>{{ snippet.before }}</span
                ><mark class="replace-content__mark">{{ snippet.match }}</mark
                ><span>{{ snippet.after }}</span>
              </p>
              <p v-if="group.count > group.snippets.length" class="replace-content__snippet">
                +{{ group.count - group.snippets.length }} more
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template #actions>
      <div class="replace-content__actions">
        <v-btn outlined color="primary" class="replace-content__btn" :disabled="!canUndo" @click="$emit('undo')">
          <v-icon size="16" class="mr-1">mdi-restore</v-icon>
          {{ $t("resources.pages.additional_sections_replace_undo") }}
        </v-btn>
        <v-btn
          color="primary"
          class="replace-content__btn replace-content__btn--primary"
          :disabled="!canReplace"
          @click="$emit('replace-all', { search, replace })">
          {{ $t("resources.pages.additional_sections_replace_all") }}
        </v-btn>
      </div>
    </template>
  </Modal>
</template>

<script>
  import Modal from "@/components/custom/Modal";
  import debounce from "lodash.debounce";
  import { scanContent } from "@/utility/replaceContent";

  const CONTEXT = 12;
  // Don't scan on a single character (avoids matching huge swaths of markup on every keystroke).
  const MIN_QUERY_LENGTH = 2;
  // Cap rendered snippets per group; the full match count is still reported.
  const MAX_SNIPPETS_PER_GROUP = 20;

  export default {
    name: "ReplaceContentModal",
    components: {
      Modal,
    },
    props: {
      value: {
        type: Boolean,
        default: false,
      },
      // [{ key, label, content }] — the searchable editors (main content + each section).
      targets: {
        type: Array,
        default: () => [],
      },
      canUndo: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        search: "",
        debouncedSearch: "",
        replace: "",
        collapsedGroups: [],
      };
    },
    computed: {
      canReplace() {
        return this.search.length >= MIN_QUERY_LENGTH;
      },
      results() {
        const term = this.debouncedSearch;

        if (!term || term.length < MIN_QUERY_LENGTH) return { groups: [], total: 0 };

        let total = 0;

        const groups = this.targets
          .map(target => {
            const { count, snippets } = scanContent(target.content, term, {
              context: CONTEXT,
              maxSnippets: MAX_SNIPPETS_PER_GROUP,
            });

            total += count;

            return { key: target.key, label: target.label, count, snippets };
          })
          .filter(group => group.count > 0);

        return { groups, total };
      },
    },
    watch: {
      value(isOpen) {
        if (isOpen) {
          this.search = "";
          this.debouncedSearch = "";
          this.replace = "";
          this.collapsedGroups = [];
        }
      },
      search: debounce(function (term) {
        this.debouncedSearch = term;
      }, 250),
    },
    methods: {
      isGroupOpen(key) {
        return !this.collapsedGroups.includes(key);
      },
      toggleGroup(key) {
        if (this.isGroupOpen(key)) {
          this.collapsedGroups = [...this.collapsedGroups, key];
        } else {
          this.collapsedGroups = this.collapsedGroups.filter(item => item !== key);
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "../../@core/preset/preset/functions.scss";
  @import "../../@core/preset/preset/typography/index.scss";

  .replace-content {
    &__panel {
      display: flex;
      flex-direction: column;
      gap: 12px;
      padding: 12px;
      background-color: get-color("hover");
      border-radius: 4px;
    }

    &__field {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    &__label {
      @include text-caption-bold();
      color: get-color("subtitle");
    }

    &__input {
      margin: 0;
      padding: 0;

      &::v-deep {
        .v-input__slot {
          background-color: get-color("element-bg-100") !important;
          min-height: 46px;
        }

        input {
          @include text-body-2();
          color: get-color("body");
        }
      }
    }

    &__count {
      @include text-caption-bold();
      color: get-color("subtitle");
    }

    &__divider {
      height: 1px;
      width: 100%;
      background-color: get-color("border");
    }

    &__results {
      display: flex;
      flex-direction: column;
      gap: 8px;
      max-height: 220px;
      overflow-y: auto;
    }

    &__group {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    &__group-head {
      display: flex;
      align-items: center;
      gap: 2px;
      cursor: pointer;
    }

    &__group-chevron {
      color: get-color("body") !important;
    }

    &__group-label {
      @include text-button-small();
      color: get-color("body");
    }

    &__snippets {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    &__snippet {
      @include text-button-small();
      color: get-color("icon");
      margin: 0;
      word-break: break-word;
    }

    &__mark {
      @include text-caption-bold();
      background-color: get-color("primary-20");
      color: get-color("body");
    }

    &__actions {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 8px;
      width: 100%;
    }

    &__btn {
      height: 38px !important;
      border-radius: 4px;
      text-transform: none;
      letter-spacing: normal;
      @include text-button-small();

      &--primary {
        @include text-button-medium();
        padding: 0 24px !important;
      }
    }
  }
</style>
